export type TaskStatus =
  | 'Not Started' | 'In Progress' | 'Blocked' | 'Awaiting Client'
  | 'In Review' | 'Done' | 'Deferred'

export type TaskOwner = 'Milestone' | 'Client' | 'Shared'

export type Task = {
  id: string
  phase: string
  owner: TaskOwner
  task: string
  status: TaskStatus
  due: string
  notes: string
  updatedBy?: string | null
  updatedAt?: { seconds: number } | null
}

export type GateStatus = 'Open' | 'At Risk' | 'Cleared'

export type GateState = {
  id: string
  status: GateStatus
  note?: string
  updatedBy?: string | null
}

export const TASK_STATUSES: TaskStatus[] = [
  'Not Started', 'In Progress', 'In Review', 'Awaiting Client', 'Blocked', 'Done', 'Deferred',
]

export const GATE_STATUSES: GateStatus[] = ['Open', 'At Risk', 'Cleared']

/** Deferred work is excluded from the denominator — it is not outstanding, it is out. */
export const isCounted = (t: Task) => t.status !== 'Deferred'
export const isComplete = (t: Task) => t.status === 'Done'

/**
 * Whether to offer somebody the button that finishes a task.
 *
 * Mirrors the third arm of the tasks rule in `firestore.rules`, and has to keep mirroring
 * it: a button whose write comes back denied is worse than no button. Client-owned only —
 * shared work is split, so one side calling it finished would declare the other side's
 * half done too — and only while it is unfinished, because this is reporting work done
 * rather than steering the board.
 *
 * `editable` wins. Somebody who already has the full status control does not also need a
 * button that sets one of its values; two controls for one field only raises the question
 * of which one is the real one.
 */
export const canMarkDone = (
  t: Task, who: { editable: boolean; canComplete: boolean },
) => !who.editable && who.canComplete && t.owner === 'Client' && !isComplete(t)

/** Work that has started but is not finished — the "actually moving" number. */
export const isActive = (t: Task) =>
  t.status === 'In Progress' || t.status === 'In Review'

/** Not moving, and not because of us. */
export const isStuck = (t: Task) =>
  t.status === 'Blocked' || t.status === 'Awaiting Client'

export const statusTone = (s: TaskStatus) =>
  s === 'Done' ? 'done'
  : s === 'Blocked' ? 'critical'
  : s === 'Awaiting Client' ? 'accent'
  : s === 'In Progress' || s === 'In Review' ? 'primary'
  : 'neutral'

export const gateTone = (s: GateStatus) =>
  s === 'Cleared' ? 'done' : s === 'At Risk' ? 'critical' : 'high'

export type PhaseProgress = {
  total: number
  done: number
  active: number
  stuck: number
  pct: number
  /** Derived, never stored — a phase status that disagrees with its tasks is a lie. */
  status: 'Not started' | 'In progress' | 'Blocked' | 'Complete'
}

export function progressFor(tasks: Task[]): PhaseProgress {
  const counted = tasks.filter(isCounted)
  const done = counted.filter(isComplete).length
  const active = counted.filter(isActive).length
  const stuck = counted.filter(isStuck).length
  const total = counted.length
  const pct = total === 0 ? 0 : (done / total) * 100

  const status: PhaseProgress['status'] =
    total === 0 ? 'Not started'
    : done === total ? 'Complete'
    : active > 0 ? 'In progress'
    : stuck > 0 ? 'Blocked'
    : done > 0 ? 'In progress'
    : 'Not started'

  return { total, done, active, stuck, pct, status }
}
